import { useEffect, useState } from "react";
import { toast } from "sonner";
import type { Lang } from "../../lib/i18n";
import { pick } from "../../lib/i18n";
import { QRCode } from "../../components/QRCode";
import { getCareerPaths, subscribeCareerPaths, type CareerPath } from "../../data/careerPaths";
import {
  getAllSystemsAdmin, subscribeSystems, upsertCustomSystem, updateSystem, deleteSystem, resetSystemOverride, resetAllSystems, setSystemEnabled, setSystemHidden, exportSystems, importSystems, newSystem,
} from "../../data/systems";
import type { AppIntegration } from "../../data/apps";
import { logAudit } from "../lib/audit";

function usePaths(): CareerPath[] {
  const [paths, setPaths] = useState(() => getCareerPaths());
  useEffect(() => subscribeCareerPaths(() => setPaths([...getCareerPaths()])), []);
  return paths;
}

function SystemEditModal({ lang, system, isNew, onClose, onSave }: { lang: Lang; system: AppIntegration; isNew: boolean; onClose: () => void; onSave: (s: AppIntegration) => void }) {
  const t = (ar: string, en: string) => pick(lang, ar, en);
  const [s, setS] = useState<AppIntegration>(system);
  const paths = usePaths();
  const set = (key: keyof AppIntegration, val: any) => setS((prev) => ({ ...prev, [key]: val }));
  const togglePath = (id: string) => {
    const has = s.paths.includes(id);
    set("paths", has ? s.paths.filter((p) => p !== id) : [...s.paths.filter((p) => p !== "all" || id === "all"), id]);
  };
  function save() {
    if (!s.id.trim() || !s.nameAr.trim() || !s.nameEn.trim()) { alert(t("يرجى تعبئة المعرّف والاسم بالعربية والإنجليزية", "Please fill ID and both Arabic and English names")); return; }
    onSave(s);
  }
  function uploadIcon(file: File | undefined) {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error(t("يُرجى اختيار ملف صورة", "Please choose an image file")); return; }
    if (file.size > 1 * 1024 * 1024) { toast.error(t("حجم الأيقونة كبير (الحد 1 ميجابايت)", "Icon too large (max 1 MB)")); return; }
    const reader = new FileReader();
    reader.onload = () => set("icon", reader.result as string);
    reader.onerror = () => toast.error(t("تعذّر قراءة الصورة", "Could not read the image"));
    reader.readAsDataURL(file);
  }
  return (
    <div className="kd-admin-modal-backdrop" onClick={onClose}>
      <div className="kd-admin-modal" onClick={(e) => e.stopPropagation()} dir={lang === "ar" ? "rtl" : "ltr"}>
        <div className="kd-admin-modal-header">
          <h2>{isNew ? t("إضافة نظام جديد", "Add New System") : t("تعديل النظام", "Edit System")}</h2>
          <button className="kd-admin-modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="kd-admin-modal-body">
          <div className="kd-admin-grid-2">
            <label><span>{t("معرّف النظام (id)", "System ID")}</span><input type="text" value={s.id} onChange={(e) => set("id", e.target.value)} disabled={!isNew} placeholder="e.g. my-app" /></label>
            <label><span>{t("لون التمييز", "Accent Color")}</span><input type="color" value={s.color} onChange={(e) => { set("color", e.target.value); set("bgColor", e.target.value + "26"); }} /></label>
          </div>
          <div className="kd-admin-grid-2">
            <label><span>{t("الاسم بالعربية", "Name (Arabic)")}</span><input type="text" value={s.nameAr} onChange={(e) => set("nameAr", e.target.value)} /></label>
            <label><span>{t("الاسم بالإنجليزية", "Name (English)")}</span><input type="text" value={s.nameEn} onChange={(e) => set("nameEn", e.target.value)} /></label>
          </div>
          <div className="kd-admin-grid-2">
            <label><span>{t("التصنيف بالعربية", "Category (Arabic)")}</span><input type="text" value={s.catAr} onChange={(e) => set("catAr", e.target.value)} /></label>
            <label><span>{t("التصنيف بالإنجليزية", "Category (English)")}</span><input type="text" value={s.catEn} onChange={(e) => set("catEn", e.target.value)} /></label>
          </div>
          <label><span>{t("الوصف بالعربية", "Description (Arabic)")}</span><textarea rows={2} value={s.descAr} onChange={(e) => set("descAr", e.target.value)} /></label>
          <label><span>{t("الوصف بالإنجليزية", "Description (English)")}</span><textarea rows={2} value={s.descEn} onChange={(e) => set("descEn", e.target.value)} /></label>
          <label><span>{t("طريقة الوصول (عربي)", "How to Access (Arabic)")}</span><textarea rows={2} value={s.accessAr} onChange={(e) => set("accessAr", e.target.value)} /></label>
          <label><span>{t("طريقة الوصول (إنجليزي)", "How to Access (English)")}</span><textarea rows={2} value={s.accessEn} onChange={(e) => set("accessEn", e.target.value)} /></label>
          <label><span>{t("رابط الويب", "Web URL")}</span><input type="url" value={s.url || ""} onChange={(e) => set("url", e.target.value)} placeholder="https://..." /></label>
          <label>
            <span>{t("مسار الأيقونة", "Icon Path")}</span>
            <div className="kd-admin-photo-upload">
              {s.icon && <img src={s.icon} alt="" className="kd-admin-icon-preview" />}
              <input type="text" value={s.icon} onChange={(e) => set("icon", e.target.value)} placeholder="/icons/myapp.png" />
              <label className="kd-admin-upload-btn">
                ⬆ {t("رفع أيقونة", "Upload icon")}
                <input type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => { uploadIcon(e.target.files?.[0]); e.target.value = ""; }} />
              </label>
            </div>
          </label>
          <fieldset className="kd-admin-fieldset">
            <legend>{t("تطبيق الجوال", "Mobile App")}</legend>
            <label className="kd-admin-checkbox"><input type="checkbox" checked={!!s.hasApp} onChange={(e) => set("hasApp", e.target.checked)} /><span>{t("يوجد تطبيق جوال لهذا النظام", "This system has a mobile app")}</span></label>
            {s.hasApp && (
              <>
                <label><span>{t("اسم التطبيق", "App Name")}</span><input type="text" value={s.appName || ""} onChange={(e) => set("appName", e.target.value)} /></label>
                <div className="kd-admin-grid-2">
                  <label><span>{t("رابط App Store (iOS)", "App Store Link (iOS)")}</span><input type="url" value={s.ios || ""} onChange={(e) => set("ios", e.target.value)} /></label>
                  <label><span>{t("رابط Google Play (Android)", "Google Play Link (Android)")}</span><input type="url" value={s.android || ""} onChange={(e) => set("android", e.target.value)} /></label>
                </div>
                <div className="kd-admin-qr-preview">
                  <p className="kd-admin-hint">{t("رمز QR يُولَّد تلقائياً للتحميل من الجوال:", "QR codes auto-generated for mobile downloads:")}</p>
                  <div className="kd-admin-qr-row">
                    {s.ios && <div style={{ textAlign: "center" }}><QRCode url={s.ios} size={80} /><div><small>App Store</small></div></div>}
                    {s.android && <div style={{ textAlign: "center" }}><QRCode url={s.android} size={80} /><div><small>Google Play</small></div></div>}
                  </div>
                </div>
              </>
            )}
          </fieldset>
          <fieldset className="kd-admin-fieldset">
            <legend>{t("المسارات المهنية التي يظهر فيها", "Career Paths Where It Appears")}</legend>
            <div className="kd-admin-paths-grid">
              <label className="kd-admin-checkbox kd-admin-path-chip"><input type="checkbox" checked={s.paths.includes("all")} onChange={() => togglePath("all")} /><span>{t("جميع الأقسام", "All Departments")}</span></label>
              {paths.map((p) => (
                <label className="kd-admin-checkbox kd-admin-path-chip" key={p.id}><input type="checkbox" checked={s.paths.includes(p.id)} onChange={() => togglePath(p.id)} /><span>{lang === "ar" ? p.ar : p.en}</span></label>
              ))}
            </div>
          </fieldset>
        </div>
        <div className="kd-admin-modal-footer">
          <button className="kd-btn-secondary" onClick={onClose}>{t("إلغاء", "Cancel")}</button>
          <button className="kd-btn-primary kd-btn-gold" onClick={save}>{t("حفظ", "Save")}</button>
        </div>
      </div>
    </div>
  );
}

/** Systems editor tab (yt) — table with toggles + add/edit modal + import/export/reset. */
export function SystemsTab({ lang }: { lang: Lang }) {
  const t = (ar: string, en: string) => pick(lang, ar, en);
  const [rows, setRows] = useState(() => getAllSystemsAdmin());
  const [editing, setEditing] = useState<{ sys: AppIntegration; isNew: boolean } | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState("");
  const paths = usePaths();
  useEffect(() => subscribeSystems(() => setRows(getAllSystemsAdmin())), []);

  function onSave(sys: AppIntegration) {
    if (editing?.isNew) { upsertCustomSystem(sys); logAudit("system_added", sys.id); }
    else { const { id, ...fields } = sys; updateSystem(id, fields); logAudit("system_updated", id); }
    setEditing(null);
  }
  function del(row: { sys: AppIntegration; isCustom: boolean }) {
    const msg = row.isCustom ? t("سيتم حذف هذا النظام نهائياً. هل أنت متأكد؟", "This system will be permanently deleted. Are you sure?") : t("سيتم إخفاء هذا النظام (يمكن استرجاعه لاحقاً). هل أنت متأكد؟", "This system will be hidden (can be restored). Are you sure?");
    if (confirm(msg)) { deleteSystem(row.sys.id); logAudit("system_deleted", row.sys.id); }
  }
  function exportConfig() {
    const blob = new Blob([exportSystems()], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = "kaden-systems-config.json"; a.click(); URL.revokeObjectURL(a.href);
  }
  async function doImport() {
    if (await importSystems(importText)) { setImportOpen(false); setImportText(""); logAudit("config_imported", "Systems config imported"); alert(t("تم استيراد الإعدادات بنجاح", "Configuration imported successfully")); }
    else alert(t("ملف غير صالح", "Invalid configuration"));
  }

  return (
    <>
      <div className="kd-admin-toolbar">
        <button className="kd-btn-primary kd-btn-gold" onClick={() => setEditing({ sys: newSystem(), isNew: true })}>+ {t("إضافة نظام جديد", "Add New System")}</button>
        <button className="kd-btn-secondary" onClick={exportConfig}>↓ {t("تصدير الإعدادات", "Export Config")}</button>
        <button className="kd-btn-secondary" onClick={() => setImportOpen(true)}>↑ {t("استيراد الإعدادات", "Import Config")}</button>
        <button className="kd-btn-secondary kd-btn-danger" onClick={() => { if (confirm(t("سيتم استعادة الإعدادات الافتراضية. متأكد؟", "Reset to defaults?"))) resetAllSystems(); }}>⟲ {t("إعادة تعيين الكل", "Reset All")}</button>
      </div>
      <div className="kd-admin-table-wrap">
        <table className="kd-admin-table">
          <thead>
            <tr><th>{t("النظام", "System")}</th><th>{t("التصنيف", "Category")}</th><th>{t("المسارات", "Paths")}</th><th>{t("تطبيق", "App")}</th><th>{t("مفعّل", "Enabled")}</th><th>{t("مرئي", "Visible")}</th><th>{t("إجراءات", "Actions")}</th></tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.sys.id} className={row.hidden ? "is-hidden-row" : ""}>
                <td>
                  <div className="kd-admin-sys-cell">
                    <img src={row.sys.icon} alt="" width={28} height={28} />
                    <div><strong>{lang === "ar" ? row.sys.nameAr : row.sys.nameEn}</strong><small>{row.sys.id} {row.isCustom && <span className="kd-admin-tag">{t("مخصّص", "Custom")}</span>}</small></div>
                  </div>
                </td>
                <td>{lang === "ar" ? row.sys.catAr : row.sys.catEn}</td>
                <td>
                  <div className="kd-admin-path-tags">
                    {row.sys.paths.map((p) => { const cp = paths.find((x) => x.id === p); return <span className="kd-admin-path-tag" key={p}>{cp ? (lang === "ar" ? cp.ar : cp.en) : p === "all" ? t("الكل", "All") : p}</span>; })}
                  </div>
                </td>
                <td>{row.sys.hasApp ? "✅" : "—"}</td>
                <td><label className="kd-admin-switch"><input type="checkbox" checked={row.enabled} onChange={(e) => setSystemEnabled(row.sys.id, e.target.checked)} /><span className="kd-admin-switch-slider" /></label></td>
                <td><label className="kd-admin-switch"><input type="checkbox" checked={!row.hidden} onChange={(e) => setSystemHidden(row.sys.id, !e.target.checked)} /><span className="kd-admin-switch-slider" /></label></td>
                <td>
                  <div className="kd-admin-actions-cell">
                    <button className="kd-admin-action-btn" onClick={() => setEditing({ sys: row.sys, isNew: false })} title="Edit">✎</button>
                    {!row.isCustom && <button className="kd-admin-action-btn" onClick={() => resetSystemOverride(row.sys.id)} title="Reset">⟲</button>}
                    <button className="kd-admin-action-btn kd-admin-action-danger" onClick={() => del(row)} title="Delete">🗑</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && <SystemEditModal lang={lang} system={editing.sys} isNew={editing.isNew} onClose={() => setEditing(null)} onSave={onSave} />}
      {importOpen && (
        <div className="kd-admin-modal-backdrop" onClick={() => setImportOpen(false)}>
          <div className="kd-admin-modal" onClick={(e) => e.stopPropagation()} dir={lang === "ar" ? "rtl" : "ltr"}>
            <div className="kd-admin-modal-header"><h2>{t("استيراد إعدادات", "Import Configuration")}</h2><button className="kd-admin-modal-close" onClick={() => setImportOpen(false)}>×</button></div>
            <div className="kd-admin-modal-body"><label><span>{t("الصق محتوى ملف JSON هنا:", "Paste JSON config here:")}</span><textarea rows={10} value={importText} onChange={(e) => setImportText(e.target.value)} /></label></div>
            <div className="kd-admin-modal-footer"><button className="kd-btn-secondary" onClick={() => setImportOpen(false)}>{t("إلغاء", "Cancel")}</button><button className="kd-btn-primary kd-btn-gold" onClick={doImport}>{t("استيراد", "Import")}</button></div>
          </div>
        </div>
      )}
    </>
  );
}
